import { ValidatorFn, AbstractControl } from '@angular/forms';
import { hasProperty, isRecord } from '@fmnts/core';

export interface EmailValidationError {
  value: string;
}

/**
 * Type guard for checking a "pattern" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has
 * "pattern" validation errors
 */
export const hasEmailError = hasProperty(
  'email',
  (u): u is EmailValidationError => isRecord(u),
);

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl<string>): { [key: string]: any } | null => {
    const isValid = isValidEmail(control.value);
    return isValid ? null : { email: { value: control.value } };
  };
}

/**
 * RegExp to check for top level domain (TLD).
 * This is used as a "heuristic" check to see if the
 * domain part obeys the LDH rule for the last 2 characters
 * and if the last character is a letter. This is
 * based on the list of all TLDs which can be found here
 * @see https://data.iana.org/TLD/tlds-alpha-by-domain.txt
 * @see https://datatracker.ietf.org/doc/html/rfc3696#section-2
 */
const tldRegex = /[a-z0-9\-]+[a-z]$/i;

/**
 * Our custom email validation function that matches the behaviour on the server
 * @param  email [description]
 * @return boolean
 */
function isValidEmail(email: string): boolean {
  // Based on RFC 2822 section 3.2.4 / RFC 5322 section 3.2.3, these
  // characters are permitted in email addresses (not taking into
  //  account internationalization):
  const ATEXT = "a-zA-Z0-9_!#\\$%&\\'\\*\\+\\-/=\\?\\^`\\{\\|\\}~";
  // A "dot atom text", per RFC 2822 3.2.4:
  const DOT_ATOM_TEXT = '[' + ATEXT + ']+(?:\\.[' + ATEXT + ']+)*';

  // The domain part of the email address, after IDNA (ASCII) encoding,
  // must also satisfy the requirements of RFC 952/RFC 1123 which restrict
  // the allowed characters of hostnames further. The hyphen cannot be at
  // the beginning or end of a *dot-atom component* of a hostname either.

  // it seems we can't match the full range \u0080-\U0010FFFF in a JavaScript regex easily
  // (we would need to use surrogate pairs
  // https://stackoverflow.com/questions/16346567/strip-u10000-u10ffff-from-javascript-strings)
  // so the local part validation is more restricted than on the backend
  //
  // // RFC 6531 section 3.3 extends the allowed characters in internationalized
  // // addresses to also include three specific ranges of UTF8 defined in
  // // RFC3629 section 4, which appear to be the Unicode code points from
  // // U+0080 to U+10FFFF.
  // var ATEXT_UTF8 = ATEXT + '\u0080-\U0010FFFF'
  // var DOT_ATOM_TEXT_UTF8 = '[' + ATEXT_UTF8 + ']+(?:\\.[' + ATEXT_UTF8 + ']+)*'

  // var ATEXT_HOSTNAME = '(?:(?:[' + validUnicodeChars + 'a-zA-Z0-9][' +
  //     validUnicodeChars + 'a-zA-Z0-9\\-]*)?[' + validUnicodeChars + 'a-zA-Z0-9])';

  // The domain part of the email address, after IDNA (ASCII) encoding,
  // must also satisfy the requirements of RFC 952/RFC 1123 which restrict
  // the allowed characters of hostnames further. The hyphen cannot be at
  // the beginning or end of a *dot-atom component* of a hostname either.
  const ATEXT_HOSTNAME = '(?:(?:[a-zA-Z0-9][a-zA-Z0-9\\-]*)?[a-zA-Z0-9])';

  if (!email) {
    return true;
  }

  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }
  try {
    validateEmailLocalPart(parts[0]);
  } catch (ex) {
    return false;
  }

  try {
    validateEmailDomainPart(parts[1]);
  } catch (ex) {
    return false;
  }

  return true;

  // *******************************************
  function validateEmailLocalPart(localPart: string) {
    if (!localPart || localPart.length === 0) {
      throw Error('There must be something before the @-sign.');
    }
    // RFC 5321 4.5.3.1.1
    if (localPart.length > 64) {
      throw Error('The email address is too long before the @-sign.');
    }
    // Check the local part against the regular expression for the older ASCII requirements.
    const re = new RegExp('^' + DOT_ATOM_TEXT + '$');
    let found = null;
    found = localPart.match(re);
    if (!found) {
      throw Error(
        'The email address contains invalid characters before the @-sign',
      );
    }
    return localPart;
  }

  function validateEmailDomainPart(domainPart: string) {
    if (!domainPart || domainPart.length === 0) {
      throw Error('There must be something after the @-sign.');
    }
    // endsWith
    if (domainPart.indexOf('.') === domainPart.length - 1) {
      throw Error('An email address cannot end with a period.');
    }
    // startsWith
    if (domainPart.indexOf('.') === 0) {
      throw Error(
        'An email address cannot have a period immediately after the @-sign.',
      );
    }
    if (domainPart.indexOf('..') !== -1) {
      throw Error('An email address cannot have two periods in a row.');
    }
    if (domainPart.length > 255) {
      throw Error('The email address is too long after the @-sign.');
    }
    // A "dot atom text", per RFC 2822 3.2.4, but using the restricted
    // characters allowed in a hostname (see ATEXT_HOSTNAME above).
    const DOT_ATOM_TEXT_2 = ATEXT_HOSTNAME + '(?:\\.' + ATEXT_HOSTNAME + ')*';
    // Check the regular expression. This is probably entirely redundant
    // with idna.decode, which also checks this format.
    const dotTextRE = new RegExp('^' + DOT_ATOM_TEXT_2 + '$');
    if (!domainPart.match(dotTextRE)) {
      throw Error(
        'The email address contains invalid characters after the @-sign.',
      );
    }
    // All publicly deliverable addresses have domain named with at least
    // one period.
    if (domainPart.indexOf('.') === -1) {
      throw Error(
        'The domain name ' +
          domainPart +
          ' is not valid. It should have a period.',
      );
    }
    // From RFC 3696 Section 2 we know that TLDs obey the LDH rule,
    // although it must not be all numeric.
    // The list of TDLs isn't fixed, but we apply a "heuristic" here
    // by checking if the domain part ends with a letter
    // preceeded by a letter, digits or hyphen
    if (domainPart.search(tldRegex) === -1) {
      throw Error(
        'The domain name ' +
          domainPart +
          ' is not valid. It is not within a valid top-level domain.',
      );
    }
    return domainPart;
  }
}
